<template>
  <div class="flex min-h-full items-center justify-center bg-canvas p-4">
    <div class="w-full max-w-md rounded-2xl border border-line bg-surface p-7 shadow-sm">
      <div class="flex flex-col items-center text-center">
        <div class="relative size-9 shrink-0">
          <div class="absolute inset-0 rounded-lg bg-brand-600"></div>
          <div class="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-sm bg-mark ring-2 ring-surface"></div>
        </div>
        <span class="mt-3 text-[15px] font-extrabold tracking-tight text-ink">Lymon</span>
        <h1 class="mt-4 text-xl font-semibold text-ink">{{ $t('signup.title') }}</h1>
        <p class="mt-1.5 text-sm text-muted">
          {{ $t('signup.signedInAs') }}
          <span class="font-medium text-ink">{{ auth.userDisplayName ?? $t('signup.verifiedUser') }}</span>
          {{ $t('signup.noWorkspaceYet') }}
        </p>
      </div>

      <form class="mt-6 space-y-4" @submit.prevent="onSubmit">
        <Field
          v-model="workspaceName"
          :label="$t('signup.workspaceNameLabel')"
          :placeholder="$t('signup.workspaceNamePlaceholder')"
          maxlength="80"
          :disabled="loading"
          :hint="$t('signup.workspaceNameHint')"
        />

        <div
          v-if="error"
          class="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger-soft p-3 text-sm text-danger"
        >
          <TriangleAlert class="size-4 shrink-0" />{{ error }}
        </div>

        <div class="flex items-center justify-between pt-2">
          <Button variant="ghost" size="sm" :disabled="loading" @click="auth.logout()">
            {{ $t('signup.signOut') }}
          </Button>
          <Button type="submit" :loading="loading">
            {{ loading ? $t('signup.creatingWorkspace') : $t('signup.createWorkspace') }}
          </Button>
        </div>
      </form>

      <p class="mt-5 text-center text-xs text-faint">
        {{ $t('signup.trialNote') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { TriangleAlert } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import Button from '../components/Button.vue';
import Field from '../components/Field.vue';

const auth = useAuthStore();
const workspaceName = ref('');
const loading = ref(false);
const error = ref<string | null>(null);

async function onSubmit(): Promise<void> {
  error.value = null;
  loading.value = true;
  try {
    await auth.signup(workspaceName.value.trim() || null);
    // refreshWhoami inside signup() flips needsSignup off, App.vue then
    // renders the main router view. No explicit navigation needed.
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    loading.value = false;
  }
}
</script>
